import { formatearFechaHora, NOMBRE_MODO_DE_PARTIDA, porcentaje } from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useTRPC } from "../api.ts";
import { Encabezado, Kpi } from "../componentes/Encabezado.tsx";
import {
  BotonEnlace,
  CABECERA,
  Enlace,
  EstadoVacio,
  Pildora,
  Tabla,
  Tarjeta,
  TD,
  TH,
} from "../componentes/ui.tsx";
import { mensajeDe } from "../utiles.ts";

export function PaginaResultadosPartida() {
  const { id } = useParams({ from: "/app/partidas/$id" });
  const trpc = useTRPC();
  const resultados = useQuery(trpc.quices.resultados.queryOptions({ id }));

  if (resultados.error) return <EstadoVacio>{mensajeDe(resultados.error)}</EstadoVacio>;
  if (!resultados.data) return <EstadoVacio>Cargando…</EstadoVacio>;
  const r = resultados.data;
  const notaMedia = r.marcador.length
    ? Math.round(r.marcador.reduce((s, j) => s + j.nota, 0) / r.marcador.length)
    : 0;
  const sinCuenta = r.marcador.filter((j) => !j.conCuenta).length;

  return (
    <div className="mx-auto max-w-[860px]">
      <Encabezado
        overline="Reto SMG"
        titulo={r.titulo}
        acciones={
          <>
            <Enlace href={`/reportes/partidas/${id}/resultados.xlsx`}>Exportar Excel</Enlace>
            <Enlace href={`/reportes/partidas/${id}/resultados.pdf`}>Exportar PDF</Enlace>
            {!r.terminada && (
              <BotonEnlace to="/partidas/$id/presentar" params={{ id }}>
                Volver a la partida
              </BotonEnlace>
            )}
          </>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2 text-[12.5px] text-texto-3">
        <Pildora tono="bg-marino-claro text-marino">{NOMBRE_MODO_DE_PARTIDA[r.modo]}</Pildora>
        <span>{formatearFechaHora(r.creadaEn)}</span>
        {!r.terminada && <span>· todavía en curso</span>}
      </div>
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <Kpi valor={r.marcador.length} etiqueta="Participantes" />
        <Kpi
          valor={`${notaMedia}%`}
          etiqueta="Nota promedio"
          tono={notaMedia >= 70 ? "verde" : "ambar"}
        />
        <Kpi valor={r.totalPreguntas} etiqueta="Preguntas" />
      </div>

      {r.marcador.length === 0 ? (
        <EstadoVacio>Nadie participó en esta partida.</EstadoVacio>
      ) : (
        <div className="space-y-5">
          <div>
            <Tabla minimo={560}>
              <thead>
                <tr className={CABECERA}>
                  <th className={TH}>Lugar</th>
                  <th className={TH}>Persona</th>
                  <th className={TH}>Aciertos</th>
                  <th className={TH}>Nota</th>
                  <th className={TH}>Puntos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borde/60">
                {r.marcador.map((j) => (
                  <tr key={j.id}>
                    <td className={TD}>{j.posicion}</td>
                    <td className={`${TD} font-semibold`}>
                      {j.nombre}
                      {!j.conCuenta && (
                        <span className="ml-2 text-[11.5px] font-normal text-texto-3">
                          sin cuenta
                        </span>
                      )}
                    </td>
                    <td className={TD}>
                      {j.aciertos} de {r.totalPreguntas}
                    </td>
                    <td className={TD}>{j.nota}%</td>
                    <td className={`${TD} font-mono`}>{j.puntos}</td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
            {sinCuenta > 0 && (
              <p className="mt-1.5 text-[11.5px] text-texto-3">
                Quien entró sin cuenta escribió su propio nombre: no está verificado.
              </p>
            )}
          </div>
          {r.preguntas.map((p, i) => (
            <Tarjeta key={p.id} titulo={`${i + 1}. ${p.texto}`}>
              <p className="mb-3 text-[12px] text-texto-3">
                Acertaron {p.aciertos} de {p.respondieron}
              </p>
              <ul className="space-y-2">
                {p.opciones.map((o) => {
                  const parte = porcentaje(o.votos, p.respondieron);
                  return (
                    <li key={o.texto}>
                      <div className="mb-0.5 flex justify-between gap-3 text-[12.5px]">
                        <span className={o.correcta ? "font-semibold text-verde" : ""}>
                          {o.texto}
                          {o.correcta && " · correcta"}
                        </span>
                        <span className="shrink-0 text-texto-3">
                          {o.votos} · {parte}%
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-gris-fondo">
                        <div
                          className={`h-full rounded-full ${o.correcta ? "bg-verde" : "bg-marino"}`}
                          style={{ width: `${parte}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Tarjeta>
          ))}
        </div>
      )}
    </div>
  );
}
